import {
  useState,
  useRef,
  useEffect,
  useMemo,
  useCallback,
  type ReactNode,
} from "react";
import {
  format,
  parse,
  isValid,
  startOfMonth,
  endOfMonth,
  startOfWeek,
  endOfWeek,
  eachDayOfInterval,
  isSameMonth,
  isSameDay,
  isToday,
  addMonths,
  subMonths,
  isBefore,
  isAfter,
  startOfDay,
} from "date-fns";
import { Calendar, ChevronLeft, ChevronRight, Clock, X } from "lucide-react";
import { cn } from "@/lib/utils";
import {
  Controller,
  type Control,
  type FieldPath,
  type FieldValues,
} from "react-hook-form";

// ────────────────────────────────────────────────────────────────────
// Internal CalendarGrid
// ────────────────────────────────────────────────────────────────────

const WEEKDAYS = ["Lu", "Ma", "Mi", "Ju", "Vi", "Sá", "Do"];

const MONTHS = [
  "Enero",
  "Febrero",
  "Marzo",
  "Abril",
  "Mayo",
  "Junio",
  "Julio",
  "Agosto",
  "Septiembre",
  "Octubre",
  "Noviembre",
  "Diciembre",
];

function CalendarGrid({
  value,
  onChange,
  minDate,
  maxDate,
}: {
  value?: Date;
  onChange: (date: Date) => void;
  minDate?: Date;
  maxDate?: Date;
}) {
  const [viewMonth, setViewMonth] = useState(() => value || new Date());

  // Sync viewMonth when value changes externally
  useEffect(() => {
    if (value && isValid(value)) setViewMonth(value);
  }, [value]);

  const days = useMemo(() => {
    const start = startOfWeek(startOfMonth(viewMonth), { weekStartsOn: 1 });
    const end = endOfWeek(endOfMonth(viewMonth), { weekStartsOn: 1 });
    return eachDayOfInterval({ start, end });
  }, [viewMonth]);

  const isDisabled = useCallback(
    (day: Date) => {
      if (minDate && isBefore(startOfDay(day), startOfDay(minDate)))
        return true;
      if (maxDate && isAfter(startOfDay(day), startOfDay(maxDate))) return true;
      return false;
    },
    [minDate, maxDate],
  );

  // Year range for selector
  const currentYear = viewMonth.getFullYear();
  const yearOptions = useMemo(() => {
    const minY = minDate ? minDate.getFullYear() : currentYear - 100;
    const maxY = maxDate ? maxDate.getFullYear() : currentYear + 10;
    const years: number[] = [];
    for (let y = maxY; y >= minY; y--) years.push(y);
    return years;
  }, [currentYear, minDate, maxDate]);

  return (
    <div className="select-none">
      {/* Month/Year navigation with dropdowns */}
      <div className="flex items-center justify-between mb-3 px-1 gap-1">
        <button
          type="button"
          onClick={() => setViewMonth((m) => subMonths(m, 1))}
          className="p-1.5 rounded-lg hover:bg-gray-100 transition-colors text-gray-600"
        >
          <ChevronLeft className="w-4 h-4" />
        </button>
        <div className="flex items-center gap-1">
          <select
            value={viewMonth.getMonth()}
            onChange={(e) => {
              const newMonth = parseInt(e.target.value);
              setViewMonth((m) => {
                const d = new Date(m);
                d.setMonth(newMonth);
                return d;
              });
            }}
            className="text-sm font-semibold text-gray-900 bg-transparent border-0 cursor-pointer hover:bg-gray-100 rounded-lg px-1.5 py-1 focus:ring-2 focus:ring-primary-500/20 focus:outline-none appearance-none capitalize"
          >
            {MONTHS.map((m, i) => (
              <option key={i} value={i}>
                {m}
              </option>
            ))}
          </select>
          <select
            value={viewMonth.getFullYear()}
            onChange={(e) => {
              const newYear = parseInt(e.target.value);
              setViewMonth((m) => {
                const d = new Date(m);
                d.setFullYear(newYear);
                return d;
              });
            }}
            className="text-sm font-semibold text-gray-900 bg-transparent border-0 cursor-pointer hover:bg-gray-100 rounded-lg px-1.5 py-1 focus:ring-2 focus:ring-primary-500/20 focus:outline-none"
          >
            {yearOptions.map((y) => (
              <option key={y} value={y}>
                {y}
              </option>
            ))}
          </select>
        </div>
        <button
          type="button"
          onClick={() => setViewMonth((m) => addMonths(m, 1))}
          className="p-1.5 rounded-lg hover:bg-gray-100 transition-colors text-gray-600"
        >
          <ChevronRight className="w-4 h-4" />
        </button>
      </div>

      {/* Weekday headers */}
      <div className="grid grid-cols-7 mb-1">
        {WEEKDAYS.map((d) => (
          <div
            key={d}
            className="text-center text-[11px] font-semibold text-gray-400 uppercase tracking-wider py-1"
          >
            {d}
          </div>
        ))}
      </div>

      {/* Day grid */}
      <div className="grid grid-cols-7 gap-0.5">
        {days.map((day) => {
          const currentMonth = isSameMonth(day, viewMonth);
          const selected = value && isSameDay(day, value);
          const today = isToday(day);
          const disabled = isDisabled(day);

          return (
            <button
              key={day.toISOString()}
              type="button"
              onClick={() => !disabled && onChange(day)}
              disabled={disabled}
              className={cn(
                "w-9 h-9 rounded-lg text-sm flex items-center justify-center transition-all duration-150 relative",
                !currentMonth && "text-gray-300",
                currentMonth &&
                  !selected &&
                  !disabled &&
                  "text-gray-700 hover:bg-primary-50 hover:text-primary-700",
                today &&
                  !selected &&
                  "font-bold text-primary-600 ring-1 ring-primary-200",
                selected &&
                  "bg-primary-600 text-white font-semibold shadow-md shadow-primary-500/30 hover:bg-primary-700",
                disabled &&
                  "text-gray-200 cursor-not-allowed hover:bg-transparent",
              )}
            >
              {format(day, "d")}
            </button>
          );
        })}
      </div>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────
// Popover wrapper
// ────────────────────────────────────────────────────────────────────

function PickerPopover({
  isOpen,
  onClose,
  children,
  containerRef,
}: {
  isOpen: boolean;
  onClose: () => void;
  children: ReactNode;
  containerRef: React.RefObject<HTMLDivElement | null>;
}) {
  useEffect(() => {
    if (!isOpen) return;
    const handleClick = (e: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(e.target as Node)
      ) {
        onClose();
      }
    };
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("mousedown", handleClick);
    document.addEventListener("keydown", handleEscape);
    return () => {
      document.removeEventListener("mousedown", handleClick);
      document.removeEventListener("keydown", handleEscape);
    };
  }, [isOpen, onClose, containerRef]);

  if (!isOpen) return null;

  return (
    <div className="absolute left-0 top-full mt-1.5 z-[100] bg-white rounded-xl shadow-2xl border border-gray-200 p-3 animate-in fade-in slide-in-from-top-2 duration-200">
      {children}
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────
// Shared trigger button style
// ────────────────────────────────────────────────────────────────────

const triggerClass = (error?: string | boolean, disabled?: boolean) =>
  cn(
    "w-full flex items-center gap-2 px-4 py-2.5 border-2 rounded-xl shadow-sm text-sm text-left",
    "transition-all duration-200 ease-out",
    "focus:ring-4 focus:ring-offset-0 focus:outline-none",
    error
      ? "border-red-400 focus:ring-red-500/20 focus:border-red-500 bg-red-50/50"
      : "border-gray-200 focus:ring-primary-500/20 focus:border-primary-500 hover:border-gray-300",
    disabled && "bg-gray-100 cursor-not-allowed opacity-60",
  );

// ────────────────────────────────────────────────────────────────────
// DatePicker (date-only)
// ────────────────────────────────────────────────────────────────────

export interface DatePickerProps {
  value?: string; // "YYYY-MM-DD"
  onChange?: (value: string) => void;
  label?: string;
  error?: string | boolean;
  hint?: string;
  placeholder?: string;
  min?: string;
  max?: string;
  disabled?: boolean;
  className?: string;
  id?: string;
  name?: string;
}

export function DatePicker({
  value,
  onChange,
  label,
  error,
  hint,
  placeholder = "dd/mm/aaaa",
  min,
  max,
  disabled,
  className,
  id,
  name,
}: DatePickerProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [textValue, setTextValue] = useState("");
  const containerRef = useRef<HTMLDivElement>(null);

  const selectedDate = useMemo(() => {
    if (!value) return undefined;
    const d = parse(value, "yyyy-MM-dd", new Date());
    return isValid(d) ? d : undefined;
  }, [value]);

  // Sync textValue when value changes (from calendar selection or external)
  useEffect(() => {
    if (selectedDate) {
      setTextValue(format(selectedDate, "dd/MM/yyyy"));
    } else if (!value) {
      setTextValue("");
    }
  }, [selectedDate, value]);

  const minDate = useMemo(() => {
    if (!min) return undefined;
    const d = parse(min, "yyyy-MM-dd", new Date());
    return isValid(d) ? d : undefined;
  }, [min]);

  const maxDate = useMemo(() => {
    if (!max) return undefined;
    const d = parse(max, "yyyy-MM-dd", new Date());
    return isValid(d) ? d : undefined;
  }, [max]);

  const handleSelect = useCallback(
    (date: Date) => {
      onChange?.(format(date, "yyyy-MM-dd"));
      setIsOpen(false);
    },
    [onChange],
  );

  const handleClear = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      onChange?.("");
      setTextValue("");
      setIsOpen(false);
    },
    [onChange],
  );

  const handleTextChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      let raw = e.target.value;
      // Auto-format: insert "/" after dd and mm
      const digits = raw.replace(/\D/g, "");
      if (digits.length <= 2) {
        raw = digits;
      } else if (digits.length <= 4) {
        raw = digits.slice(0, 2) + "/" + digits.slice(2);
      } else {
        raw =
          digits.slice(0, 2) +
          "/" +
          digits.slice(2, 4) +
          "/" +
          digits.slice(4, 8);
      }
      setTextValue(raw);

      // Try to parse complete date
      if (digits.length === 8) {
        const parsed = parse(raw, "dd/MM/yyyy", new Date());
        if (isValid(parsed)) {
          const iso = format(parsed, "yyyy-MM-dd");
          onChange?.(iso);
        }
      }
    },
    [onChange],
  );

  const handleTextBlur = useCallback(() => {
    // On blur, if there's a partial value, try to parse or revert
    if (!textValue) {
      onChange?.("");
      return;
    }
    const parsed = parse(textValue, "dd/MM/yyyy", new Date());
    if (isValid(parsed) && textValue.length === 10) {
      onChange?.(format(parsed, "yyyy-MM-dd"));
    } else if (selectedDate) {
      // Revert to last valid date
      setTextValue(format(selectedDate, "dd/MM/yyyy"));
    } else {
      setTextValue("");
      onChange?.("");
    }
  }, [textValue, selectedDate, onChange]);

  const inputId = id || name;
  const errorId = error ? `${inputId}-error` : undefined;
  const hintId = hint && !error ? `${inputId}-hint` : undefined;

  return (
    <div ref={containerRef} className={cn("relative w-full", className)}>
      {label && (
        <label
          htmlFor={inputId}
          className="block text-sm font-medium text-gray-700 mb-1"
        >
          {label}
        </label>
      )}

      <div className={cn(triggerClass(error, disabled), "relative")}>
        <Calendar
          className="w-4 h-4 text-gray-400 shrink-0 cursor-pointer"
          onClick={() => !disabled && setIsOpen(!isOpen)}
        />
        <input
          id={inputId}
          type="text"
          inputMode="numeric"
          placeholder={placeholder}
          value={textValue}
          onChange={handleTextChange}
          onBlur={handleTextBlur}
          onFocus={() => !disabled && setIsOpen(true)}
          disabled={disabled}
          autoComplete="off"
          aria-invalid={!!error}
          aria-describedby={
            [errorId, hintId].filter(Boolean).join(" ") || undefined
          }
          className="flex-1 bg-transparent border-0 outline-none text-sm text-gray-900 placeholder:text-gray-400 p-0 focus:ring-0"
        />
        {selectedDate && !disabled && (
          <span
            role="button"
            tabIndex={-1}
            onClick={handleClear}
            className="p-0.5 hover:bg-gray-100 rounded-md transition-colors"
          >
            <X className="w-3.5 h-3.5 text-gray-400" />
          </span>
        )}
      </div>

      <PickerPopover
        isOpen={isOpen}
        onClose={() => setIsOpen(false)}
        containerRef={containerRef}
      >
        <CalendarGrid
          value={selectedDate}
          onChange={handleSelect}
          minDate={minDate}
          maxDate={maxDate}
        />
        {/* Quick today button */}
        <div className="border-t border-gray-100 mt-2 pt-2 flex justify-center">
          <button
            type="button"
            onClick={() => handleSelect(new Date())}
            className="text-xs text-primary-600 hover:text-primary-700 font-medium hover:bg-primary-50 px-3 py-1 rounded-lg transition-colors"
          >
            Hoy
          </button>
        </div>
      </PickerPopover>

      {hint && !error && (
        <p id={hintId} className="mt-1 text-sm text-gray-500">
          {hint}
        </p>
      )}
      {typeof error === "string" && error && (
        <p id={errorId} className="mt-1 text-sm text-red-600" role="alert">
          {error}
        </p>
      )}
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────
// DateTimePicker (date + time)
// ────────────────────────────────────────────────────────────────────

export interface DateTimePickerProps {
  value?: string; // "YYYY-MM-DDTHH:mm"
  onChange?: (value: string) => void;
  label?: string;
  error?: string | boolean;
  hint?: string;
  placeholder?: string;
  min?: string;
  max?: string;
  disabled?: boolean;
  className?: string;
  id?: string;
  name?: string;
}

export function DateTimePicker({
  value,
  onChange,
  label,
  error,
  hint,
  placeholder = "dd/mm/aaaa hh:mm",
  min,
  max,
  disabled,
  className,
  id,
  name,
}: DateTimePickerProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [textValue, setTextValue] = useState("");
  const containerRef = useRef<HTMLDivElement>(null);

  const parseDT = (v: string | undefined) => {
    if (!v) return undefined;
    const d = new Date(v);
    return isValid(d) ? d : undefined;
  };

  const selectedDate = useMemo(() => parseDT(value), [value]);
  const minDate = useMemo(() => parseDT(min), [min]);
  const maxDate = useMemo(() => parseDT(max), [max]);

  const [hours, setHours] = useState(() =>
    selectedDate ? String(selectedDate.getHours()).padStart(2, "0") : "12",
  );
  const [minutes, setMinutes] = useState(() =>
    selectedDate ? String(selectedDate.getMinutes()).padStart(2, "0") : "00",
  );

  // Sync text and time fields when value changes
  useEffect(() => {
    if (selectedDate) {
      setTextValue(format(selectedDate, "dd/MM/yyyy HH:mm"));
      setHours(String(selectedDate.getHours()).padStart(2, "0"));
      setMinutes(String(selectedDate.getMinutes()).padStart(2, "0"));
    } else if (!value) {
      setTextValue("");
    }
  }, [selectedDate, value]);

  const emitValue = useCallback(
    (date: Date, h: string, m: string) => {
      const hNum = Math.min(23, Math.max(0, parseInt(h) || 0));
      const mNum = Math.min(59, Math.max(0, parseInt(m) || 0));
      const dt = new Date(date);
      dt.setHours(hNum, mNum, 0, 0);
      onChange?.(format(dt, "yyyy-MM-dd'T'HH:mm"));
    },
    [onChange],
  );

  const handleDaySelect = useCallback(
    (date: Date) => {
      emitValue(date, hours, minutes);
    },
    [emitValue, hours, minutes],
  );

  const handleTimeChange = useCallback(
    (h: string, m: string) => {
      setHours(h);
      setMinutes(m);
      if (selectedDate) {
        emitValue(selectedDate, h, m);
      }
    },
    [selectedDate, emitValue],
  );

  const handleClear = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      onChange?.("");
      setTextValue("");
      setIsOpen(false);
    },
    [onChange],
  );

  const handleTextChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      let raw = e.target.value;
      // Auto-format: dd/mm/yyyy hh:mm
      const digits = raw.replace(/\D/g, "");
      if (digits.length <= 2) {
        raw = digits;
      } else if (digits.length <= 4) {
        raw = digits.slice(0, 2) + "/" + digits.slice(2);
      } else if (digits.length <= 8) {
        raw =
          digits.slice(0, 2) + "/" + digits.slice(2, 4) + "/" + digits.slice(4);
      } else if (digits.length <= 10) {
        raw =
          digits.slice(0, 2) +
          "/" +
          digits.slice(2, 4) +
          "/" +
          digits.slice(4, 8) +
          " " +
          digits.slice(8);
      } else {
        raw =
          digits.slice(0, 2) +
          "/" +
          digits.slice(2, 4) +
          "/" +
          digits.slice(4, 8) +
          " " +
          digits.slice(8, 10) +
          ":" +
          digits.slice(10, 12);
      }
      setTextValue(raw);

      // Try to parse complete datetime (12 digits = ddMMyyyyHHmm)
      if (digits.length >= 12) {
        const dateStr =
          digits.slice(0, 2) +
          "/" +
          digits.slice(2, 4) +
          "/" +
          digits.slice(4, 8);
        const parsed = parse(dateStr, "dd/MM/yyyy", new Date());
        if (isValid(parsed)) {
          const h = digits.slice(8, 10);
          const m = digits.slice(10, 12);
          setHours(h);
          setMinutes(m);
          emitValue(parsed, h, m);
        }
      }
    },
    [emitValue],
  );

  const handleTextBlur = useCallback(() => {
    if (!textValue) {
      onChange?.("");
      return;
    }
    // Try parse "dd/MM/yyyy HH:mm"
    const parsed = parse(textValue, "dd/MM/yyyy HH:mm", new Date());
    if (isValid(parsed) && textValue.length >= 16) {
      onChange?.(format(parsed, "yyyy-MM-dd'T'HH:mm"));
    } else if (selectedDate) {
      setTextValue(format(selectedDate, "dd/MM/yyyy HH:mm"));
    } else {
      setTextValue("");
      onChange?.("");
    }
  }, [textValue, selectedDate, onChange]);

  const inputId = id || name;
  const errorId = error ? `${inputId}-error` : undefined;
  const hintId = hint && !error ? `${inputId}-hint` : undefined;

  return (
    <div ref={containerRef} className={cn("relative w-full", className)}>
      {label && (
        <label
          htmlFor={inputId}
          className="block text-sm font-medium text-gray-700 mb-1"
        >
          {label}
        </label>
      )}

      <div className={cn(triggerClass(error, disabled), "relative")}>
        <Calendar
          className="w-4 h-4 text-gray-400 shrink-0 cursor-pointer"
          onClick={() => !disabled && setIsOpen(!isOpen)}
        />
        <input
          id={inputId}
          type="text"
          inputMode="numeric"
          placeholder={placeholder}
          value={textValue}
          onChange={handleTextChange}
          onBlur={handleTextBlur}
          onFocus={() => !disabled && setIsOpen(true)}
          disabled={disabled}
          autoComplete="off"
          aria-invalid={!!error}
          aria-describedby={
            [errorId, hintId].filter(Boolean).join(" ") || undefined
          }
          className="flex-1 bg-transparent border-0 outline-none text-sm text-gray-900 placeholder:text-gray-400 p-0 focus:ring-0"
        />
        {selectedDate && !disabled && (
          <span
            role="button"
            tabIndex={-1}
            onClick={handleClear}
            className="p-0.5 hover:bg-gray-100 rounded-md transition-colors"
          >
            <X className="w-3.5 h-3.5 text-gray-400" />
          </span>
        )}
      </div>

      <PickerPopover
        isOpen={isOpen}
        onClose={() => setIsOpen(false)}
        containerRef={containerRef}
      >
        <CalendarGrid
          value={selectedDate}
          onChange={handleDaySelect}
          minDate={minDate}
          maxDate={maxDate}
        />

        {/* Time selector */}
        <div className="border-t border-gray-100 mt-2 pt-3 flex items-center justify-center gap-2">
          <Clock className="w-4 h-4 text-gray-400" />
          <input
            type="number"
            min={0}
            max={23}
            value={hours}
            onChange={(e) => handleTimeChange(e.target.value, minutes)}
            className="w-14 text-center py-1.5 border-2 border-gray-200 rounded-lg text-sm font-medium focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500 outline-none transition-all"
          />
          <span className="text-gray-400 font-bold text-lg">:</span>
          <input
            type="number"
            min={0}
            max={59}
            value={minutes}
            onChange={(e) => handleTimeChange(hours, e.target.value)}
            className="w-14 text-center py-1.5 border-2 border-gray-200 rounded-lg text-sm font-medium focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500 outline-none transition-all"
          />
        </div>

        <div className="border-t border-gray-100 mt-2 pt-2 flex justify-between">
          <button
            type="button"
            onClick={() => {
              const now = new Date();
              setHours(String(now.getHours()).padStart(2, "0"));
              setMinutes(String(now.getMinutes()).padStart(2, "0"));
              emitValue(
                selectedDate || now,
                String(now.getHours()),
                String(now.getMinutes()),
              );
            }}
            className="text-xs text-primary-600 hover:text-primary-700 font-medium hover:bg-primary-50 px-3 py-1 rounded-lg transition-colors"
          >
            Ahora
          </button>
          <button
            type="button"
            onClick={() => setIsOpen(false)}
            className="text-xs text-gray-500 hover:text-gray-700 font-medium hover:bg-gray-50 px-3 py-1 rounded-lg transition-colors"
          >
            Listo
          </button>
        </div>
      </PickerPopover>

      {hint && !error && (
        <p id={hintId} className="mt-1 text-sm text-gray-500">
          {hint}
        </p>
      )}
      {typeof error === "string" && error && (
        <p id={errorId} className="mt-1 text-sm text-red-600" role="alert">
          {error}
        </p>
      )}
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────
// React Hook Form wrappers
// ────────────────────────────────────────────────────────────────────

export interface DatePickerFieldProps<T extends FieldValues> extends Omit<
  DatePickerProps,
  "value" | "onChange"
> {
  control: Control<T>;
  name: FieldPath<T>;
}

export function DatePickerField<T extends FieldValues>({
  control,
  name,
  ...rest
}: DatePickerFieldProps<T>) {
  return (
    <Controller
      control={control}
      name={name}
      render={({ field }) => (
        <DatePicker
          name={name}
          value={field.value}
          onChange={field.onChange}
          {...rest}
        />
      )}
    />
  );
}

export interface DateTimePickerFieldProps<T extends FieldValues> extends Omit<
  DateTimePickerProps,
  "value" | "onChange"
> {
  control: Control<T>;
  name: FieldPath<T>;
}

export function DateTimePickerField<T extends FieldValues>({
  control,
  name,
  ...rest
}: DateTimePickerFieldProps<T>) {
  return (
    <Controller
      control={control}
      name={name}
      render={({ field }) => (
        <DateTimePicker
          name={name}
          value={field.value}
          onChange={field.onChange}
          {...rest}
        />
      )}
    />
  );
}
